import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';

// "Send the email again" for an account whose confirmation link was never
// clicked. Used on the Check your email page and under the login form when
// Supabase says the email is not confirmed.
//
// It says plainly whether the email went: a green line when Supabase accepted
// it, a red one with Supabase's own reason when it did not. Supabase allows one
// confirmation email a minute per address, so after a send the button counts
// down rather than inviting a second click that would only be refused.

const COOLDOWN_SECONDS = 60;

// Callers pass key={email}: a different address is a different send, so the
// last answer and countdown start over.
export default function ResendVerification({ email }) {
  const { resendVerification } = useAuth();
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);   // { ok: boolean, text: string } | null
  const [wait, setWait] = useState(0);          // seconds until the button works again

  useEffect(() => {
    if (wait <= 0) return undefined;
    const t = setTimeout(() => setWait(w => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  const send = async () => {
    setSending(true);
    setResult(null);
    const { error } = await resendVerification(email);
    setSending(false);
    if (error) {
      setResult({ ok: false, text: `We couldn't send it: ${error.message}` });
      return;
    }
    setResult({ ok: true, text: `Sent. A new confirmation email is on its way to ${email}. Check your spam folder too.` });
    setWait(COOLDOWN_SECONDS);
  };

  const disabled = sending || wait > 0 || !email;
  const label = sending ? 'Sending...' : wait > 0 ? `Send again in ${wait}s` : 'Send the email again';

  return (
    <div style={{ marginBottom: 'var(--space-lg)' }}>
      <button
        type="button"
        onClick={send}
        disabled={disabled}
        style={{
          padding: '10px 18px', borderRadius: 'var(--radius-10)', border: '1px solid var(--brand-sand)',
          background: 'var(--brand-white)', color: disabled ? 'var(--neutral-400)' : 'var(--neutral-800)',
          fontSize: 'var(--font-size-base)', fontWeight: 600, fontFamily: 'inherit',
          cursor: disabled ? 'default' : 'pointer',
        }}
      >
        {label}
      </button>
      {result && (
        <p
          role={result.ok ? 'status' : 'alert'}
          style={{ fontSize: 'var(--font-size-base)', lineHeight: 1.5, margin: '10px 0 0', color: result.ok ? '#15803d' : 'var(--feedback-error)' }}
        >
          {result.text}
        </p>
      )}
    </div>
  );
}
